import { skipToken } from '@reduxjs/toolkit/query';
import { useFetchActiveProcessesQuery } from '../service';
import { useEffect, useRef } from 'react';
import { URL_BATCH_ANALYSIS_PROCESS_POLLING_TIME_MS } from '../../settings';
import { deepAnalysisApi } from '../../service';
import { useAppDispatch } from '../../../useAppDispatch';

export default function useBatchUrlAnalysisProcessesPolling(websiteGuid: string | null) {
    const dispatch = useAppDispatch();
    const isBatchUrlAnalysisProcessActive = useRef<boolean>(false);

    const { data: runningBatchUrlAnalysisProcesses, isLoading: isLoadingRunningProcesses } = useFetchActiveProcessesQuery(
        websiteGuid ? { w_guid: websiteGuid, process_type: 'on_page_seo_batch_url_analysis' } : skipToken,
        {
            pollingInterval: URL_BATCH_ANALYSIS_PROCESS_POLLING_TIME_MS,
            refetchOnMountOrArgChange: true,
        }
    );

    const hasUrlAnalysisProcesses = !!runningBatchUrlAnalysisProcesses && runningBatchUrlAnalysisProcesses.length > 0;
    const hasNoUrlAnalysisProcesses = !!runningBatchUrlAnalysisProcesses && runningBatchUrlAnalysisProcesses.length === 0;

    useEffect(() => {
        // Had no batch process but now there are
        if (hasUrlAnalysisProcesses && isBatchUrlAnalysisProcessActive.current === false) {
            isBatchUrlAnalysisProcessActive.current = true;
        }

        // Had batch processes but they are finished
        if (hasNoUrlAnalysisProcesses && isBatchUrlAnalysisProcessActive.current === true) {
            isBatchUrlAnalysisProcessActive.current = false;

            if (websiteGuid) {
                dispatch(
                    deepAnalysisApi.util.invalidateTags([
                        { type: 'UrlAudits', id: websiteGuid },
                        { type: 'ServiceableUrls', id: websiteGuid },
                        { type: 'ServiceableUrlsInCluster', id: websiteGuid },
                        { type: 'AffectedUrls', id: websiteGuid },
                        { type: 'FailedChecks', id: websiteGuid },
                    ])
                );
            }
        }
    }, [dispatch, hasNoUrlAnalysisProcesses, hasUrlAnalysisProcesses, websiteGuid]);

    const isRunningBatchAnalysis = !!runningBatchUrlAnalysisProcesses && runningBatchUrlAnalysisProcesses.length > 0;

    return {
        activeBatchUrlAnalysisProcess: runningBatchUrlAnalysisProcesses ? runningBatchUrlAnalysisProcesses[0] : null,
        isRunningBatchAnalysis,
        isLoadingRunningProcesses,
    };
}
